import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Paper,
  CircularProgress
} from '@mui/material';
import kc_v from '../kc_v.jpg';
import api from '../services/api';
import Captcha from './Captcha';

const ForgetPassword = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaResetTrigger, setCaptchaResetTrigger] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [step, setStep] = useState('request'); // 'request' or 'reset'
  const navigate = useNavigate();

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setStep('request');
    setEmail('');
    setPhone('');
    setOtp('');
    setNewPassword('');
    setResetToken('');
    setCaptchaVerified(false);
  };

  const handleEmailRequest = async () => {
    if (!email) {
      setSnackbar({ open: true, message: 'Please enter your email', severity: 'error' });
      return;
    }

    if (!captchaVerified) {
      setSnackbar({ open: true, message: 'Please verify the CAPTCHA first', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/forget-password-email', { email });
      setResetToken(response.data.resetToken); // In production, this would be sent via email
      setStep('reset');
      setSnackbar({ open: true, message: 'Reset link sent to your email', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to send reset link', severity: 'error' });
      // Reset captcha on failure
      setCaptchaResetTrigger(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter' && captchaVerified) {
      handleEmailRequest();
    }
  };

  const handlePhoneRequest = async () => {
    if (!phone) {
      setSnackbar({ open: true, message: 'Please enter your phone number', severity: 'error' });
      return;
    }

    if (!captchaVerified) {
      setSnackbar({ open: true, message: 'Please verify the CAPTCHA first', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/forget-password-phone', { phone });
      setStep('reset');
      setSnackbar({ open: true, message: `OTP sent to your phone: ${response.data.otp}`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to send OTP', severity: 'error' });
      // Reset captcha on failure
      setCaptchaResetTrigger(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneKeyPress = (e) => {
    if (e.key === 'Enter' && captchaVerified) {
      handlePhoneRequest();
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setSnackbar({ open: true, message: 'Please enter a new password', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      if (activeTab === 0) {
        // Email reset
        await api.post('/auth/reset-password-token', { resetToken, newPassword });
      } else {
        // Phone reset
        if (!otp) {
          setSnackbar({ open: true, message: 'Please enter the OTP', severity: 'error' });
          setLoading(false);
          return;
        }
        await api.post('/auth/reset-password-otp', { phone, otp, newPassword });
      }
      
      setSnackbar({ open: true, message: 'Password reset successfully!', severity: 'success' });
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to reset password', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        overflow: 'hidden',
        '::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${kc_v})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(4px) brightness(0.9)',
          zIndex: -1
        }
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: '400px',
          p: 3,
          borderRadius: 2,
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          zIndex: 1
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Forgot Password
        </Typography>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Email" />
          <Tab label="Phone" />
        </Tabs>

        {step === 'request' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {activeTab === 0 ? (
              <>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleEmailKeyPress}
                  required
                />
                <Captcha onVerify={setCaptchaVerified} resetTrigger={captchaResetTrigger} />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleEmailRequest}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                </Button>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyPress={handlePhoneKeyPress}
                  required
                />
                <Captcha onVerify={setCaptchaVerified} resetTrigger={captchaResetTrigger} />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handlePhoneRequest}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Send OTP'}
                </Button>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {activeTab === 1 && (
              <TextField
                fullWidth
                label="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />
            )}
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleResetPassword}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Reset Password'}
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button onClick={() => navigate('/login')} color="primary">
            Back to Login
          </Button>
        </Box>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ForgetPassword; 